import React, { useEffect, useState } from 'react';
import { LocalizedLink } from '../../components/LocalizedLink';
import { useAdminStore } from '../../stores/adminStore';
import { useTranslation } from '../../hooks/useTranslation';
import { toLocalizedFormat, UNIVERSAL_DATE_FORMAT } from '@dutchinvoice/shared-utils';
import { useInvoiceStore } from '../../stores/invoiceStore';
import { format } from 'date-fns';
import { format as timeAgo } from 'timeago.js';
import { adminApi } from '../../lib/api';

export const EmailQueue: React.FC = () => {
  const { t } = useTranslation();
  const language = useInvoiceStore(state => state.settings.language);
  const { emailQueue, emailQueueLoading, emailQueueError, fetchEmailQueue } = useAdminStore();
  const [sendingEmails, setSendingEmails] = useState<Record<string, boolean>>({});
  const [sendResults, setSendResults] = useState<Record<string, { success: boolean, message?: string }>>({});
  const [downloadingPDFs, setDownloadingPDFs] = useState<Record<string, boolean>>({});
  const [requeueingEmails, setRequeuingEmails] = useState<Record<string, boolean>>({});
  const [requeueResults, setRequeueResults] = useState<Record<string, { success: boolean, message?: string }>>({});

  useEffect(() => {
    // Fetch email queue data when component mounts
    fetchEmailQueue();
  }, [fetchEmailQueue]);

  const handleSendEmail = async (emailId: string) => {
    setSendingEmails(prev => ({ ...prev, [emailId]: true }));
    
    try {
      await adminApi.sendEmail(emailId);
      setSendResults(prev => ({ ...prev, [emailId]: { success: true, message: 'Email sent successfully' } }));
      
      // Clear result and refresh queue after delay
      setTimeout(() => {
        fetchEmailQueue();
        setSendResults(prev => {
          const newPrev = { ...prev };
          delete newPrev[emailId];
          return newPrev;
        });
      }, 2000);
    } catch (error) {
      console.error('Error sending email:', error);
      setSendResults(prev => ({ ...prev, [emailId]: { success: false, message: 'Failed to send email' } }));
    } finally {
      setSendingEmails(prev => ({ ...prev, [emailId]: false }));
    }
  };

  const handleDownloadPDF = async (emailId: string) => {
    setDownloadingPDFs(prev => ({ ...prev, [emailId]: true }));
    
    try {
      const response = await adminApi.downloadInvoicePDF(emailId);
      
      // Create a blob and download it
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Open PDF in new tab
      window.open(url, '_blank');
      
      // Clean up the object URL after a short delay
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF');
    } finally {
      setDownloadingPDFs(prev => ({ ...prev, [emailId]: false }));
    }
  };

  const handleRequeue = async (emailId: string) => {
    setRequeuingEmails(prev => ({ ...prev, [emailId]: true }));
    
    try {
      await adminApi.requeueEmail(emailId);
      setRequeueResults(prev => ({ ...prev, [emailId]: { success: true, message: 'Email requeued successfully' } }));
      
      // Clear result and refresh queue after delay
      setTimeout(() => {
        fetchEmailQueue();
        setRequeueResults(prev => {
          const newPrev = { ...prev };
          delete newPrev[emailId];
          return newPrev;
        });
      }, 2000);
    } catch (error) {
      console.error('Error requeuing email:', error);
      setRequeueResults(prev => ({ ...prev, [emailId]: { success: false, message: 'Failed to requeue email' } }));
    } finally {
      setRequeuingEmails(prev => ({ ...prev, [emailId]: false }));
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
            {t('admin.emails.status.sent')}
          </span>
        );
      case 'planned':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
            {t('admin.emails.status.planned')}
          </span>
        );
      case 'failed':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
            {t('admin.emails.status.failed')}
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <LocalizedLink
              to="/admin"
              className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('admin.backToAdmin')}
            </LocalizedLink>
            <h1 className="text-2xl font-semibold text-gray-900">{t('admin.emails.title')}</h1>
          </div>
          <button 
            onClick={() => fetchEmailQueue()} 
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {t('admin.emails.refresh')}
          </button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">{t('admin.emails.total')}</div>
            <div className="text-3xl font-semibold text-gray-900">{emailQueue.length}</div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">{t('admin.emails.planned')}</div>
            <div className="text-3xl font-semibold text-gray-900">
              {emailQueue.filter(email => email.status === 'planned').length}
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">{t('admin.emails.sent')}</div>
            <div className="text-3xl font-semibold text-gray-900">
              {emailQueue.filter(email => email.status === 'sent').length}
            </div>
          </div>
        </div>

        {/* Email Queue Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-medium text-gray-900">{t('admin.emails.queueTitle')}</h2>
          </div>
          
          {emailQueueLoading ? (
            <div className="p-6 text-center text-gray-500">{t('common.loading')}</div>
          ) : emailQueueError ? (
            <div className="p-6 text-center text-red-500">{emailQueueError}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.emails.invoiceNumber')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.emails.user')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.emails.recipient')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.emails.scheduled')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.emails.sent')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.emails.attempts')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {emailQueue.map((email) => {
                    // Add safe date parsing with error handling
                    let scheduledAtDate = '';
                    let sentAtDate = null;
                    
                    try {
                      // Handle both string dates and Unix timestamps
                      const scheduledAt = typeof email.scheduled_at === 'number' || /^\d+$/.test(email.scheduled_at) 
                        ? new Date(parseInt(String(email.scheduled_at)) * 1000) 
                        : new Date(email.scheduled_at);
                      
                      if (!isNaN(scheduledAt.getTime())) {
                        scheduledAtDate = format(scheduledAt, UNIVERSAL_DATE_FORMAT);
                      }
                      
                      if (email.sent_at) {
                        const sentAt = typeof email.sent_at === 'number' || /^\d+$/.test(email.sent_at)
                          ? new Date(parseInt(String(email.sent_at)) * 1000)
                          : new Date(email.sent_at);
                          
                        if (!isNaN(sentAt.getTime())) {
                          sentAtDate = format(sentAt, UNIVERSAL_DATE_FORMAT);
                        }
                      }
                    } catch (error) {
                      console.error('Error parsing date:', error);
                    }
                    
                    return (
                      <tr key={email.id} className="hover:bg-gray-50/50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {email.invoice_number || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {email.user_email || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {email.recipient_email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {getStatusBadge(email.status as string)}
                          {email.error && (
                            <div className="text-xs text-red-500 mt-1">{email.error}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {scheduledAtDate ? (
                            <>
                              {toLocalizedFormat(scheduledAtDate, language)}
                              <div className="text-xs text-gray-400 mt-0.5">
                                {typeof email.scheduled_at === 'string' && !isNaN(Date.parse(email.scheduled_at)) 
                                  ? timeAgo(email.scheduled_at) 
                                  : '-'}
                              </div>
                            </>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {sentAtDate ? (
                            <>
                              {toLocalizedFormat(sentAtDate, language)}
                              <div className="text-xs text-gray-400 mt-0.5">
                                {typeof email.sent_at === 'string' && !isNaN(Date.parse(email.sent_at))
                                  ? timeAgo(email.sent_at)
                                  : '-'}
                              </div>
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {email.attempts}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {email.status === 'planned' || email.status === 'sent' || email.status === 'failed' ? (
                            <div className="flex flex-col space-y-2">
                              <button
                                onClick={() => handleSendEmail(email.id)}
                                disabled={sendingEmails[email.id]}
                                className={`text-xs px-3 py-1.5 rounded font-medium transition-colors ${
                                  sendingEmails[email.id]
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                                }`}
                              >
                                {sendingEmails[email.id] ? (
                                  <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Sending...
                                  </span>
                                ) : (
                                  email.status === 'sent' ? "Send Again" : "Send Now"
                                )}
                              </button>
                              
                              <button
                                onClick={() => handleDownloadPDF(email.id)}
                                disabled={downloadingPDFs[email.id]}
                                className={`text-xs px-3 py-1.5 rounded font-medium transition-colors ${
                                  downloadingPDFs[email.id]
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                {downloadingPDFs[email.id] ? (
                                  <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Loading PDF...
                                  </span>
                                ) : (
                                  "Download PDF"
                                )}
                              </button>
                              
                              {/* Show requeue button only for failed emails */}
                              {email.status === 'failed' && (
                                <button
                                  onClick={() => handleRequeue(email.id)}
                                  disabled={requeueingEmails[email.id]}
                                  className={`text-xs px-3 py-1.5 rounded font-medium transition-colors ${
                                    requeueingEmails[email.id]
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                                  }`}
                                >
                                  {requeueingEmails[email.id] ? (
                                    <span className="flex items-center">
                                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      Requeuing...
                                    </span>
                                  ) : (
                                    "Requeue"
                                  )}
                                </button>
                              )}
                              
                              {sendResults[email.id] && (
                                <div className={`mt-1.5 text-xs px-2 py-1 rounded ${
                                  sendResults[email.id].success 
                                    ? 'bg-green-50 text-green-700' 
                                    : 'bg-red-50 text-red-700'
                                }`}>
                                  {sendResults[email.id].message}
                                </div>
                              )}
                              
                              {requeueResults[email.id] && (
                                <div className={`mt-1.5 text-xs px-2 py-1 rounded ${
                                  requeueResults[email.id].success 
                                    ? 'bg-green-50 text-green-700' 
                                    : 'bg-red-50 text-red-700'
                                }`}>
                                  {requeueResults[email.id].message}
                                </div>
                              )}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 